import React, { useEffect, useState } from "react";
import { ArrowLeft, Check, ChevronRight, RotateCcw, Sparkles } from "lucide-react";
import type { WorkbenchEvidence, WorkbenchFix } from "@/types/workbench";
import { Modal, Textarea, Callout, Avatar } from "@/components/vb";
import { renderMarkdownSafe } from "@/lib/markdown";
import { providerMeta } from "@/pages/vb/run/providerMeta";
import { fixLifecycle, formatShortDate, formatMonth } from "@/pages/workbench/workbenchUtil";
// TODO(MW-3, future prop): "Draft with MarcusAI" — per-step grounded drafting is
// built (MarcusDraft + workbenchClient.streamDraft, backed by the MW-3 SSE draft
// endpoint) but held back from this release. Re-enable by restoring the import
// and the <MarcusDraft/> block below (and its companyId/token/workspaceId props).
// Until then, the <WorkbenchSoon/> teaser stands in for it. This replaced the
// interim "open in your chatbot" per-step menu, which sent owners out of the app.
// import { MarcusDraft } from "./MarcusDraft";

type Props = {
  fix: WorkbenchFix;
  companyId: number;
  token: string | null;
  workspaceId: string | number | null;
  /** created_at of the report the queue is built from — reconciles a stale
   *  "done" (regressed) against the latest scan. */
  reportDate?: string | null;
  busy: boolean;
  /** Return to the fix queue. On mobile the detail is the full-screen primary
   *  view (no split panel), so it renders a back control. WorkbenchPage always
   *  passes this; on desktop the control is simply CSS-hidden (the queue stays
   *  visible beside the detail). */
  onBack?: () => void;
  onToggleStep: (stepIndex: number, done: boolean) => void;
  onMarkDone: () => void;
  onReopen: () => void;
  onSaveNotes: (notes: string) => void;
};

const SEV_LABEL: Record<string, string> = { critical: "Critical", issue: "Issue" };
const SCORE_LABEL: Record<string, string> = {
  fail: "Failing",
  low: "Low",
  medium: "Medium",
  high: "High",
  pass: "Passing",
};

/** The right-hand pane: why the fix matters, what AI said, the step checklist,
 *  a "MarcusAI drafting is coming" teaser, notes, and the mark-done footer. */
export function ActiveFix({
  fix,
  // companyId, token, workspaceId — reserved for the future MarcusAI draft (see TODO above)
  reportDate,
  busy,
  onBack,
  onToggleStep,
  onMarkDone,
  onReopen,
  onSaveNotes,
}: Props) {
  const lifecycle = fixLifecycle(fix, reportDate);
  const done = lifecycle === "done";
  const regressed = lifecycle === "regressed";
  const completedOn = formatShortDate(fix.task.completed_at);
  const stepsDone = fix.steps.filter((s) => s.done).length;
  const stepsTotal = fix.steps.length;
  const pct = stepsTotal > 0 ? Math.round((stepsDone / stepsTotal) * 100) : done ? 100 : 0;

  const eyebrowCls = done ? "done" : regressed ? "recheck" : fix.severity;
  const eyebrowLabel = done ? "Done" : regressed ? "Rechecked" : SEV_LABEL[fix.severity] ?? fix.severity;

  return (
    <div className="wb-fix">
      {/* Mobile-only: the detail is the full-screen primary view, so it needs a way
          back to the queue. Hidden on desktop (split panel) via the container query. */}
      {onBack && (
        <button type="button" className="wb-fix-back" onClick={onBack}>
          <ArrowLeft size={16} aria-hidden="true" />
          Back to fixes
        </button>
      )}

      {/* ── Why + evidence ── */}
      <section className="wb-fix-card">
        <header className="wb-fix-hd">
          <span className="wb-fix-hd__eyebrow">
            <span className={`wb-sev wb-sev--${eyebrowCls}`}>{eyebrowLabel}</span>
            AI Trust Signal fix
            <span className="wb-tier">{fix.tier_label}</span>
          </span>
          <h2>{fix.title}</h2>
          <div className="wb-fix-hd__meta">
            <span className="wb-fix-hd__tech">{fix.tech}</span>
            {fix.score && SCORE_LABEL[fix.score] && <span>· Currently {SCORE_LABEL[fix.score]}</span>}
          </div>
        </header>

        {/* A fix marked done before this report ran, yet still flagged by it —
            surface it as active work again rather than silently "done". */}
        {regressed && (
          <div className="wb-recheck" role="status">
            <RotateCcw size={15} aria-hidden="true" />
            <span>
              You marked this done{completedOn ? ` on ${completedOn}` : ""}, but your{" "}
              {formatMonth(reportDate)} report still shows AI getting this wrong. Reopen it to
              pick up where you left off.
            </span>
          </div>
        )}

        {fix.why && (
          <div
            className="wb-why vb-md"
            dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(fix.why) }}
          />
        )}

        {fix.evidence.length > 0 && <EvidenceBlock evidence={fix.evidence} />}
      </section>

      {/* ── Steps ── */}
      <section className="wb-fix-card">
        <span className="wb-fix-hd__eyebrow">Your plan</span>
        {stepsTotal === 0 ? (
          fix.summary ? (
            <div
              className="wb-step__desc vb-md"
              dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(fix.summary) }}
            />
          ) : (
            <p className="wb-step__desc">No steps for this fix yet.</p>
          )
        ) : (
          <div className="wb-steps">
            {fix.steps.map((step, i) => (
              <div key={i} className={`wb-step${step.done ? " wb-step--done" : ""}`}>
                <button
                  type="button"
                  className="wb-step__tick"
                  aria-pressed={step.done}
                  aria-label={step.done ? `Mark step ${i + 1} not done` : `Mark step ${i + 1} done`}
                  onClick={() => onToggleStep(i, !step.done)}
                >
                  <Check size={14} strokeWidth={3} />
                </button>
                <div className="wb-step__body">
                  <span className="wb-step__n">Step {i + 1}</span>
                  <span className="wb-step__title">{step.title}</span>
                  {step.detail && (
                    <div
                      className="wb-step__desc vb-md"
                      dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(step.detail) }}
                    />
                  )}
                  {/* TODO(MW-3, future prop): grounded "Draft with MarcusAI" for this
                      step. Component + endpoint are ready; held back for this release.
                      Re-enable together with the import at the top of the file (and
                      remove the <WorkbenchSoon/> teaser below).
                  {step.marcus_prompt.trim() && (
                    <MarcusDraft
                      companyId={companyId}
                      signalKey={fix.signal_key}
                      stepIndex={i}
                      token={token}
                      workspaceId={workspaceId}
                      label={step.title}
                    />
                  )} */}
                </div>
              </div>
            ))}
          </div>
        )}

        <WorkbenchSoon />

        <NotesEditor
          key={fix.signal_key}
          initial={fix.task.notes}
          busy={busy}
          onSave={onSaveNotes}
        />
      </section>

      {/* ── Footer ── */}
      <footer className="wb-fix-ft">
        <span className="wb-fix-ft__status">
          {done ? (
            <span className="wb-fix-ft__done">
              <Check size={13} strokeWidth={3} aria-hidden="true" />
              Completed{completedOn ? ` ${completedOn}` : ""} · re-checked on your next report
            </span>
          ) : (
            stepsTotal > 0 && (
              <>
                <span className="wb-fix-ft__bar" aria-hidden="true">
                  <span style={{ width: `${pct}%` }} />
                </span>
                <span className="tnum">
                  {stepsDone}/{stepsTotal} steps
                </span>
              </>
            )
          )}
        </span>
        <span className="wb-fix-ft__spacer" />
        {done ? (
          <button type="button" className="vb-btn" onClick={onReopen} disabled={busy}>
            <RotateCcw size={14} />
            Reopen fix
          </button>
        ) : regressed ? (
          // Stored status is still "done"; re-marking done wouldn't re-stamp the
          // completion. Reopening resets it to clean open work (clears the stale
          // date), then the normal "Mark fix as done" flow applies.
          <button type="button" className="vb-btn vb-btn--primary" onClick={onReopen} disabled={busy}>
            <RotateCcw size={14} />
            Reopen to re-work
          </button>
        ) : (
          <button type="button" className="vb-btn vb-btn--primary" onClick={onMarkDone} disabled={busy}>
            <Check size={14} />
            Mark fix as done
          </button>
        )}
      </footer>
    </div>
  );
}

/** A single, quiet "coming soon" teaser shown once per fix (not per step). It
 *  stands in for the future in-app "Draft with MarcusAI" and replaces the interim
 *  per-step "open in your chatbot" menu we removed — the point is to keep owners
 *  working inside the app, not to hand them off to an external tool. Static: no
 *  LLM call, no network. */
function WorkbenchSoon() {
  return (
    <Callout tone="brand" dashed role="note" icon={<Sparkles size={15} />}>
      <strong>Drafting with MarcusAI is coming soon.</strong> You'll generate ready-to-use
      fixes for each step right here in your workspace.
    </Callout>
  );
}

/** How many characters of an answer we show inline before clamping + offering
 *  "See full response". Short answers render in full with no button. */
const EVIDENCE_CLAMP_CHARS = 300;

/** AI evidence: the best-grounded answer up front (markdown-rendered, clamped
 *  when long), the rest behind a toggle. "See full response" opens the complete
 *  answer in a dialog — just the response, no prompt. */
function EvidenceBlock({ evidence }: { evidence: WorkbenchEvidence[] }) {
  const [showAll, setShowAll] = useState(false);
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const shown = showAll ? evidence : evidence.slice(0, 1);
  const rest = evidence.length - 1;
  const active = openIdx != null ? evidence[openIdx] : null;

  return (
    <div>
      {shown.map((ev, i) => {
        const m = providerMeta(ev.provider);
        const text = ev.response.trim();
        const long = text.length > EVIDENCE_CLAMP_CHARS;
        return (
          <div className="wb-aiq" key={i}>
            <div className="wb-aiq__top">
              <Avatar model={ev.provider} size="sm" decorative />
              {m.label} said
            </div>
            {text ? (
              <div
                className={`vb-md wb-aiq__body${long ? " wb-aiq__body--clamp" : ""}`}
                dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(text) }}
              />
            ) : (
              <p className="wb-aiq__empty">No answer was returned by this model.</p>
            )}
            {long && (
              <button type="button" className="wb-aiq__more" onClick={() => setOpenIdx(i)}>
                See full response
                <ChevronRight size={13} aria-hidden="true" />
              </button>
            )}
          </div>
        );
      })}
      {rest > 0 && (
        <div className="wb-draft__tools" style={{ marginTop: 8 }}>
          <button type="button" className="vb-btn vb-btn--sm" onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Show less" : `Show ${rest} other AI answer${rest === 1 ? "" : "s"}`}
          </button>
        </div>
      )}

      <Modal
        open={active != null}
        onOpenChange={(o) => !o && setOpenIdx(null)}
        srTitle="Full AI response"
        widthClassName="max-w-2xl"
      >
        {active && <EvidenceResponse ev={active} />}
      </Modal>
    </div>
  );
}

/** The full, markdown-rendered AI answer shown in the "See full response" dialog
 *  — the response only, never the prompt we sent. */
export function EvidenceResponse({ ev }: { ev: WorkbenchEvidence }) {
  const m = providerMeta(ev.provider);
  const text = ev.response.trim();
  return (
    <div className="wb-resp">
      <div className="wb-aiq__top wb-resp__hd">
        <Avatar model={ev.provider} size="sm" decorative />
        <span>{m.label} said</span>
        <span className="wb-resp__model">{m.label}</span>
      </div>
      {text ? (
        <div className="vb-md" dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(text) }} />
      ) : (
        <p className="wb-aiq__empty">No answer was returned by this model.</p>
      )}
    </div>
  );
}

/** Free-text notes for the fix, saved on demand (MW-2 `notes`). */
function NotesEditor({
  initial,
  busy,
  onSave,
}: {
  initial: string;
  busy: boolean;
  onSave: (notes: string) => void;
}) {
  const [value, setValue] = useState(initial ?? "");
  useEffect(() => setValue(initial ?? ""), [initial]);
  const dirty = value !== (initial ?? "");
  return (
    <div className="wb-notes">
      <label className="wb-notes__lbl" htmlFor="wb-notes-input">
        Notes
      </label>
      <Textarea
        id="wb-notes-input"
        placeholder="Jot down what you changed, links, who's on it…"
        value={value}
        maxLength={10000}
        onChange={(e) => setValue(e.target.value)}
      />
      {dirty && (
        <button
          type="button"
          className="vb-btn vb-btn--sm wb-notes__save"
          onClick={() => onSave(value)}
          disabled={busy}
        >
          Save notes
        </button>
      )}
    </div>
  );
}

export default ActiveFix;
